import React from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { Checkbox } from '@cotera/client/v0/components/ui/checkbox';
import { cn } from '@cotera/client/v0/lib/utils';
import type { Column } from './resource-list-view-types';

export function ResourceListColgroup({
  columns,
  hasTreeColumn = false,
  hasTrailingActionsColumn = false,
}: {
  columns: Column[];
  /** Mirrors the 20px disclosure column tree rows render ahead of the icon. */
  hasTreeColumn?: boolean;
  /** Mirrors the 44px trailing ⋯ column on rows that have actions. */
  hasTrailingActionsColumn?: boolean;
}): React.ReactElement {
  return (
    <colgroup>
      {hasTreeColumn ? <col style={{ width: '20px' }} /> : null}
      {/* Matches the 40px leading icon column in the rows (grid-cols-[40px_1fr]),
          which is sized for the color-coded resource icon bubble (h-8). */}
      <col style={{ width: '40px' }} />
      {columns.map((col) => (
        <col
          key={col.columnKey ?? col.label}
          style={col.width !== undefined ? { width: col.width } : undefined}
        />
      ))}
      {hasTrailingActionsColumn ? <col style={{ width: '44px' }} /> : null}
    </colgroup>
  );
}

/**
 * One header cell. The bottom rule lives here rather than on the `<tr>`, where it
 * used to sit: the table is `border-separate`, and a row border is never painted
 * under that model, so every list built on this header lost the rule Automations
 * has (its header is a plain flex div, not a table). `border-spacing-0` keeps the
 * per-cell borders flush, so they read as one continuous line.
 */
const HEADER_CELL_CLASS =
  'border-b border-border py-2 px-3 text-xs font-normal text-muted-foreground whitespace-nowrap';

export type ColumnSort = {
  activeSort: string | undefined;
  sortDirection: 'asc' | 'desc';
  onSortSelect: (key: string) => void;
};

/** `aria-sort` for a header cell, so the sort state is announced, not just drawn. */
function ariaSortFor(
  col: Column,
  sort: ColumnSort | undefined
): 'ascending' | 'descending' | 'none' | undefined {
  if (col.sortKey === undefined || sort === undefined) {
    return undefined;
  }
  if (sort.activeSort !== col.sortKey) {
    return 'none';
  }
  return sort.sortDirection === 'asc' ? 'ascending' : 'descending';
}

/**
 * A header label that toggles sorting when the column declares a `sortKey`.
 * The arrow only renders for the active column; inactive sortable columns show
 * a muted hint on hover so the affordance is discoverable without adding noise
 * to every header.
 */
const SortableHeaderLabel: React.FC<{
  col: Column;
  sort: ColumnSort;
}> = ({ col, sort }) => {
  const sortKey = col.sortKey;
  if (sortKey === undefined) {
    return <>{col.label}</>;
  }
  const isActive = sort.activeSort === sortKey;
  return (
    <button
      type="button"
      onClick={() => sort.onSortSelect(sortKey)}
      className={cn(
        // The negative margins cancel the padding: it is hit target and focus
        // ring, not layout. Without `-my-0.5` this cell's label sat 2px below
        // the plain-text headers next to it, because they have no button to pad
        // — the sorted column read as a different, lower, heavier thing.
        'group/sort inline-flex items-center gap-1 rounded-sm -mx-1 px-1 -my-0.5 py-0.5',
        'transition-colors hover:text-foreground focus-visible:outline-none',
        'focus-visible:ring-2 focus-visible:ring-ring cursor-pointer',
        // Active is a shade, not a weight. The arrow beside it already says
        // which column is sorted; `font-medium` on top of that made "Updated"
        // read as a size of its own next to "Created by" and "Shared with".
        isActive && 'text-foreground'
      )}
    >
      <span>{col.label}</span>
      {isActive ? (
        sort.sortDirection === 'asc' ? (
          <ArrowUp className="h-3 w-3 shrink-0" aria-hidden="true" />
        ) : (
          <ArrowDown className="h-3 w-3 shrink-0" aria-hidden="true" />
        )
      ) : (
        <ChevronsUpDown
          className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover/sort:opacity-60"
          aria-hidden="true"
        />
      )}
    </button>
  );
};

/**
 * The leading column's header text: a sort control where the column is
 * sortable, plain text otherwise, and a screen-reader-only stand-in where the
 * surface honours {@link Column.labelHidden}.
 *
 * `hideLabel` is the caller's answer, not the column's, because the two headers
 * disagree: the stacked header honours the flag, the column table draws the
 * label regardless. See {@link Column.labelHidden}.
 *
 * The hidden case deliberately skips {@link SortableHeaderLabel} rather than
 * wrapping an empty span in it, which would leave a button with nothing to
 * click and a hover chevron floating over blank header.
 */
const FirstHeaderLabel: React.FC<{
  col: Column | undefined;
  sort: ColumnSort | undefined;
  hideLabel: boolean;
}> = ({ col, sort, hideLabel }) => {
  if (col === undefined) {
    return null;
  }
  if (hideLabel) {
    return <span className="sr-only">{col.label}</span>;
  }
  if (sort !== undefined) {
    return <SortableHeaderLabel col={col} sort={sort} />;
  }
  return <>{col.label}</>;
};

export function ResourceListColumnHeaders({
  columns,
  colgroup,
  compact = false,
  selectAll,
  sort,
  hasTreeColumn = false,
  hasTrailingActionsColumn = false,
}: {
  columns: Column[];
  colgroup: React.ReactNode;
  compact?: boolean;
  /**
   * When provided, renders a "select all" checkbox in the left margin (aligned
   * with the per-row checkboxes). It is always rendered for lists that support
   * bulk actions — gating it on "something is already selected" made the entry
   * point to select-all reachable only after selecting a row by hand.
   */
  selectAll?: {
    state: boolean | 'indeterminate';
    onToggle: () => void;
  };
  /** Enables click-to-sort on columns that declare a `sortKey`. */
  sort?: ColumnSort;
  /** Tree rows carry an extra leading column; the header must span it too. */
  hasTreeColumn?: boolean;
  /** Rows carry a trailing ⋯ column; the header reserves the same slot. */
  hasTrailingActionsColumn?: boolean;
}): React.ReactElement {
  const selectAllControl =
    selectAll !== undefined ? (
      // Left margin, aligned with the per-row checkboxes (which sit just left of
      // the icon column). Quiet until hovered/focused/active so the header reads
      // calmly, but always present and always keyboard reachable.
      <span
        className={cn(
          'absolute right-full mr-2 top-1/2 z-10 flex -translate-y-1/2 items-center',
          'transition-opacity',
          selectAll.state === false
            ? 'opacity-0 hover:opacity-100 focus-within:opacity-100 has-[:focus-visible]:opacity-100'
            : 'opacity-100'
        )}
      >
        <Checkbox
          checked={selectAll.state}
          aria-label={selectAll.state === true ? 'Deselect all' : 'Select all'}
          onCheckedChange={() => selectAll.onToggle()}
        />
      </span>
    ) : null;
  const firstColumn = columns[0];
  return (
    <div className="relative group/header">
      {selectAllControl}
      <div
        className={cn(
          'border-b border-border',
          compact === true ? 'block' : '@list-cols/list:hidden'
        )}
      >
        {/* Narrow layout: this label is the only thing the header draws, so a
            hidden one collapses the box to the rule above the rows rather than
            leaving 16px of padding around nothing. */}
        <div
          className={cn(
            'text-xs text-muted-foreground font-normal text-left whitespace-nowrap',
            firstColumn?.labelHidden === true ? null : 'py-2 px-3'
          )}
        >
          <FirstHeaderLabel
            col={firstColumn}
            sort={sort}
            hideLabel={firstColumn?.labelHidden === true}
          />
        </div>
        {columns.slice(1).some((c) => c.headerContent !== undefined) ? (
          <div className="px-3 pb-2 flex flex-wrap gap-2 justify-end">
            {columns.slice(1).map((col) =>
              col.headerContent !== undefined ? (
                <div
                  key={col.columnKey ?? col.label}
                  className="min-w-0 flex justify-end"
                >
                  {col.headerContent}
                </div>
              ) : null
            )}
          </div>
        ) : null}
      </div>
      <table
        className={cn(
          'w-full table-fixed border-separate border-spacing-0',
          compact === true ? 'hidden' : 'hidden @list-cols/list:table'
        )}
      >
        {colgroup}
        <thead>
          <tr>
            <th
              colSpan={hasTreeColumn ? 3 : 2}
              aria-sort={
                firstColumn !== undefined
                  ? ariaSortFor(firstColumn, sort)
                  : undefined
              }
              className={cn(HEADER_CELL_CLASS, 'text-left')}
            >
              <FirstHeaderLabel
                col={firstColumn}
                sort={sort}
                hideLabel={false}
              />
            </th>
            {columns.slice(1).map((col) => (
              <th
                key={col.columnKey ?? col.label}
                style={
                  col.width !== undefined ? { width: col.width } : undefined
                }
                aria-sort={ariaSortFor(col, sort)}
                className={cn(HEADER_CELL_CLASS, 'align-top text-left')}
                aria-label={
                  col.headerAriaLabel !== undefined
                    ? col.headerAriaLabel
                    : undefined
                }
              >
                {col.headerContent !== undefined ? (
                  <div className="flex flex-col gap-1.5 items-start">
                    {col.label !== '' ? <span>{col.label}</span> : null}
                    {col.headerContent}
                  </div>
                ) : sort !== undefined && col.sortKey !== undefined ? (
                  <SortableHeaderLabel col={col} sort={sort} />
                ) : (
                  col.label
                )}
              </th>
            ))}
            {/* Reserves the ⋯ slot so header labels stay aligned over their
                columns; intentionally unlabelled. */}
            {hasTrailingActionsColumn ? (
              <th aria-hidden="true" className={HEADER_CELL_CLASS} />
            ) : null}
          </tr>
        </thead>
      </table>
    </div>
  );
}
